import { gql, type TypedDocumentNode } from '@apollo/client';
import { useIntl } from 'react-intl';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { Scores } from '#src/atoms/icons/Scores.tsx';
import { ANY_SPORT_SERIES_MATCHES } from '#src/constants/__generated__/routes.ts';
import { useEnableArcadeGameList } from '#src/hooks/commonGame/useEnableArcadeGameList.ts';
import { useIsInTraining } from '#src/hooks/commonGame/useIsInTraining.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsDesktopAndAbove } from '#src/hooks/device/useIsDesktopAndAbove.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { navLabels } from '#src/lib/glossary.ts';
import { isType } from '#src/lib/gql/index.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';

import { NavItemWithSubNav } from '../NavItemWithSubNav/index.tsx';
import { useNavItemLayout } from '../useNavItemLayout.ts';
import type {
  SeriesMatchesNavItemQuery,
  SeriesMatchesNavItemQueryVariables,
} from './__generated__/index.graphql.ts';

const SERIES_MATCHES_NAV_ITEM_QUERY = gql`
  query SeriesMatchesNavItemQuery($sport: Sport!) {
    currentUser {
      slug
      setBoard(mode: CAREER, sport: $sport) {
        id
        ... on CareerBoard {
          id
          steps {
            id
            ... on CareerStep {
              id
              myTask {
                id
                ... on ThresholdPickerTask {
                  id
                  averageMode
                }
              }
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  SeriesMatchesNavItemQuery,
  SeriesMatchesNavItemQueryVariables
>;

type Props = { sport: Sport; sidebarCollapsed?: boolean };

export const SeriesMatchesNavItem = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const { navItemsVariant, iconSize } = useNavItemLayout();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const seriesNav = useSeriesNav();
  const isInTraining = useIsInTraining();
  const isArcadeGameListEnabled = useEnableArcadeGameList();
  const canShowTab = seriesNav && !isDesktopAndAbove && isArcadeGameListEnabled;
  const { data } = useQuery(SERIES_MATCHES_NAV_ITEM_QUERY, {
    variables: { sport },
    skip: !canShowTab,
  });
  const setBoard = data?.currentUser?.setBoard;
  const isAverageModeBoard =
    isType(setBoard, 'CareerBoard') &&
    setBoard.steps.some(
      step =>
        isType(step, 'CareerStep') &&
        isType(step.myTask, 'ThresholdPickerTask') &&
        step.myTask.averageMode
    );

  if (!canShowTab || !data || isAverageModeBoard) {
    return null;
  }

  return (
    <NavItemWithSubNav
      item={{
        variant: navItemsVariant,
        icon: ({ isActive }) => <Scores size={iconSize} active={isActive} />,
        label: formatMessage(navLabels.matches),
        locked: isInTraining,
        to: generateSportPath(ANY_SPORT_SERIES_MATCHES, { sport }),
        enableContextModeNavigation: false,
        enabledStoredLastNavigation: false,
        sidebarCollapsed,
      }}
    />
  );
};
